import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

import { getGeocode, getLatLng } from 'use-places-autocomplete'

import { useCreateMeetingLinkAvailabilityMutation, useGetMeetingLinkQuery } from '@/core/services/meeting-link'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import CreateAvailabilityWizard, { AvailabilityWizardValues } from '../components/availability-wizard/CreateAvailabilityWizard'

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(timezone)

type CreateAvailabilityWizardContainerProps = {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function CreateAvailabilityWizardContainer({
  open,
  onClose,
  onSuccess,
}: CreateAvailabilityWizardContainerProps) {
  const { meetingLinkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: meetingLink } = useGetMeetingLinkQuery(meetingLinkId ?? '', { skip: !meetingLinkId })
  const [createMeetingLinkAvailability] = useCreateMeetingLinkAvailabilityMutation()

  const specialities = useMemo(() => _.map(meetingLink?.specialities, 'name'), [meetingLink])

  const resolveServiceArea = async (values: AvailabilityWizardValues) => {
    if (values.meetingPlace !== AvailabilityMeetingPlace.AT_HOME || !values.serviceArea) return undefined

    const allowedPostalCodes = typeof values.serviceArea.allowedPostalCodes === 'string'
      ? values.serviceArea.allowedPostalCodes.split(',').map((s) => s.trim()).filter(Boolean)
      : []

    let center = values.serviceArea.center
    if (center?.placeId && (center.lat == null || center.lng == null)) {
      const results = await getGeocode({ placeId: center.placeId })
      const { lat, lng } = getLatLng(results[0])
      center = { ...center, lat, lng }
    }

    return { allowedPostalCodes, center, radiusKm: values.serviceArea.radiusKm || undefined }
  }

  const handleSubmit = async (values: AvailabilityWizardValues) => {
    if (!meetingLinkId) return
    setIsSubmitting(true)

    try {
      const serviceArea = await resolveServiceArea(values)

      await createMeetingLinkAvailability({
        meetingLinkId,
        availability: {
          address: values.address,
          additionalAddress: values.additionalAddress,
          avgMeetingDuration: Number(values.avgMeetingDuration),
          meetingPlace: values.meetingPlace,
          specialities: values.specialities,
          notes: values.notes,
          formTemplate: values.formTemplate || undefined,
          isRecurrence: true,
          recurrenceDays: _.map(values.recurrenceDays, (recurrenceDay) => ({
            day: recurrenceDay.day,
            periods: _.map(recurrenceDay.periods, (period) => ({
              open: dayjs.duration({
                hours: Number(period.open.split(':')[0]),
                minutes: Number(period.open.split(':')[1]),
              }).asMinutes(),
              close: dayjs.duration({
                hours: Number(period.close.split(':')[0]),
                minutes: Number(period.close.split(':')[1]),
              }).asMinutes(),
            })),
            timeZone: dayjs.tz.guess(),
          })),
          scheduledDays: [],
          serviceArea,
          paymentConfig: undefined,
        },
      }).unwrap()

      enqueueSnackbar(t('notification.availability.create.success'), { variant: 'success' })
      onClose()
      onSuccess?.()
    } catch {
      enqueueSnackbar(t('notification.availability.create.error'), { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ 'data-testid': 'availability-wizard' }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('page.meetingLink.wizard.dialogTitle', 'New Availability')}
        <IconButton onClick={onClose} size="small" aria-label={t('common:button.close.label', 'Close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <CreateAvailabilityWizard
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          specialities={specialities}
        />
      </DialogContent>
    </Dialog>
  )
}
